// The Sales Order packaging plan's pallet actions: ONE copy, used by the desktop Sales Order page
// (pages/SalesOrderPage.jsx, SOPackagingPlanCard) and the phone order screen
// (mobile/screens/SalesOrderDetailScreen.jsx). docs/mobile-pwa-plan.md "One copy of every action".
// Moved from SOPackagingPlanCard word for word; its tests (SalesOrderPage.pallets.actions.test.jsx)
// pin the prompts, requests and PDFs.
//
// `ui` (features/desktopUi.js | mobile/ui/phoneUi.js): pin, confirm, and openPdfTab(url, filename):
// the desktop opens a PDF in a new tab and prints it; a phone has none (null), so an action returns
// the PDF instead (`labels` to send to a label printer, `document` to open from a tap) and the
// screen offers it. Labels from a phone go through the shop print queue (printLabelsAtLaser).
//
// The page's own state: setBusy(true|false) around the request, setMessage(text) for the one-line
// result ('' clears it), reloadPlan() to re-read the plan, openFinalize({kind, pin, preActionBits})
// when the pallet just shipped / staged was the order's last (the desktop's SO-Finalize email
// dialog). Every action resolves to {done, message?, labels?, document?, finalize?}.
import config from '../../config.js';
import { detailToString } from '../../utils/apiError';
import { palletDisplayName, wipDrainNote } from './palletRules';
import { promotedText } from './holdingPallets';
import { withEditor } from './planEdits';
import { shopToday } from '../../utils/shopDate';
import { labelStation } from '../print/labelStation';

const API_BASE = config.API_BASE_URL;
const noop = () => {};

function postJson(path, body) {
  return fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function readOk(res) {
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(detailToString(b.detail) || `HTTP ${res.status}`);
  }
  return res.json();
}

const errText = (err) => err.message || String(err);

/** Who seals (Phase 1 shipping redesign, 2026-10-27): a ui with identity(why) (the Shipping hub's Working-as session)
 *  names the person; the seal body then carries `pin` and jobq-api records them as sealed_by. When that ui gets no
 *  answer (sign-in cancelled) the seal stops: null. A ui without identity (the SO page, the phone): the body is
 *  exactly as before ({sealed_by: null}). */
async function sealBody(ui) {
  if (typeof ui.identity !== 'function') return { sealed_by: null };
  const pin = await ui.identity('seal');
  return pin ? { sealed_by: null, pin } : null;
}

export const SEAL_NEEDS_SIGN_IN = 'Not sealed: sign in to seal.';

/** The API refuses a seal / ship / stage while the pallet's parts still owe an in-house step (409 "Still owes
 *  Finish: ..."; jobq-api _enforce_inhouse_stage_gate, 2026-10-27: PLT-XB80 read READY TO SEAL with Finish and
 *  Brake open). The refusal is shown; a manager can let it through with a PIN and a reason (audited). */
export const INHOUSE_REFUSAL = /^Still owes /;

/** Ask for a manager's override of an in-house refusal: {inhouse_override_pin, inhouse_override_reason}, or null
 *  when the person declines (nothing is resent). */
async function askInhouseOverride(detail, ui, what) {
  const go = await ui.confirm(
    `${detail}\n\nA manager can let this ${what} through now (manager PIN + reason). Do that?`,
    { title: /\bat the \S+ vendor\b/i.test(detail) ? 'An outside step is still open' : 'An in-house step is still open', confirmLabel: 'Manager override' },
  );
  if (!go) return null;
  // typed every time, never a face: a manager's PIN AUTHORISES here (an admin session must not approve by itself)
  const managerPin = await ui.pin(`Manager PIN to ${what} before the in-house step is done.`, { autofill: false, face: false });
  if (!managerPin) return null;
  const reason = await ui.prompt(`Why does it ${what} before the in-house step is done? (at least 5 characters)`);
  if (!reason || !String(reason).trim()) return null;
  return { inhouse_override_pin: managerPin, inhouse_override_reason: String(reason).trim() };
}

export async function postOrOverride(path, body, ui, what) {
  const res = await postJson(path, body);
  if (res.status !== 409) return readOk(res);
  const b = await res.json().catch(() => ({}));
  const detail = detailToString(b.detail) || `HTTP ${res.status}`;
  if (!INHOUSE_REFUSAL.test(detail)) throw new Error(detail);
  const answer = await askInhouseOverride(detail, ui, what);
  if (!answer) throw new Error(detail);
  return readOk(await postJson(path, { ...body, ...answer }));
}

/** The same offer for a caller that reads the Response itself (the Sales Order page's Ship all / Stage all, which
 *  tell a refusal from a 5xx): resolves to the Response to read, the override's when a manager let it through. */
export async function postWithInhouseOverride(path, body, ui, what) {
  const res = await postJson(path, body);
  if (res.status !== 409) return res;
  const b = await (res.clone ? res.clone() : res).json().catch(() => ({}));
  const detail = detailToString(b.detail) || '';
  if (!INHOUSE_REFUSAL.test(detail)) return res;
  const answer = await askInhouseOverride(detail, ui, what);
  if (!answer) return res;
  return postJson(path, { ...body, ...answer });
}

/** The load-out checklist (features/pallets/planPalletFlows, 2026-10-26): {items} | {items: null} | {cancelled}.
 *  Loaded on demand so the order screens don't pull the dialog in until a pallet leaves. */
async function loadOut(args) {
  const { loadOutChecklist } = await import('../pallets/planPalletFlows');
  return loadOutChecklist(args);
}
const withLoad = (body, lo) => (lo && Array.isArray(lo.items) ? { ...body, load_checklist: lo.items } : body);
const loadedNote = (body) => {
  const n = body && body.load_checklist && Array.isArray(body.load_checklist.recorded)
    ? body.load_checklist.recorded.length : 0;
  return n ? `${n} checked off as loaded` : '';
};

/** "Email the customer?" after the PIN. A phone's ui.ask can be closed without an answer: that
 *  backs out of the whole action (null). The desktop's window.confirm always answers. */
async function askNotify(ui, desktopText, phoneText) {
  if (ui.ask) return ui.ask(phoneText, { title: 'Email the customer?', yesLabel: 'Send email', noLabel: 'Skip email' });
  return ui.confirm(desktopText);
}

/** The seal-box question. A phone prints nothing by itself: it offers the label for its printer (the Laser Zebra
 *  unless its ui names another station: ../print/labelStation). */
export function sealBoxQuestion(boxLabel, palletName, { opensPdf = true, printer = 'Laser Zebra' } = {}) {
  return `Seal ${boxLabel} on Pallet ${palletName}?\n\n`
    + '  • Records the box in the shipment system (assigns a batch ID the first time)\n'
    + (opensPdf
      ? "  • Opens just this box's label PDF (print + tape to the box lid)\n"
      : `  • Then you can send this box's label to the ${printer} (tape it to the box lid)\n`)
    + '  • Locks the box — no more laser tasks can route parts into it\n\n'
    + 'Seal the pallet at the end to get the pallet + stack labels.';
}

export function sealPalletQuestion(palletName, orderNumber, { opensPdf = true, printer = 'Laser Zebra' } = {}) {
  return `Seal Pallet ${palletName} for ${orderNumber}?\n\n`
    + 'This will:\n'
    + "  • Finalize any remaining boxes that aren't already sealed\n"
    + '  • Record pallet-direct (loose) stacks in the shipment system\n'
    + (opensPdf
      ? '  • Open the pallet label + stack labels PDF (print + apply before wrapping)\n'
      : `  • Then you can send the pallet + stack labels to the ${printer} (apply before wrapping)\n`)
    + '  • Mark the pallet Ready to ship\n\n'
    + "The pallet is NOT shipped yet — that's the next button.";
}

export const SHIP_NOTIFY_QUESTION = 'Send the shipping notification email to the customer now?\n\n'
  + 'OK = send email with manifest attached\n'
  + 'Cancel = finalize the shipment but skip the email';

// The same questions with named buttons (a phone's ui.ask): Send email / Skip email.
export const SHIP_NOTIFY_ASK = 'Send the shipping notification email to the customer now, with the manifest attached?\n\n'
  + 'Skip email finalizes the shipment without it. Close to stop: nothing ships.';

export function stageNotifyAsk(palletName) {
  return `Send the customer a "Ready for Pickup" email for pallet ${palletName} now, with the pallet manifest attached?\n\n`
    + "Skip email stages it without emailing (typical when you'll Finalize the SO at the end). Close to stop: nothing is staged.";
}

export function stageNotifyQuestion(palletName) {
  return `Send the customer a "Ready for Pickup" email for pallet ${palletName}?\n\n`
    + 'OK = send the pickup notification email now (with the pallet manifest attached)\n'
    + "Cancel = stage the pallet without emailing (typical when you'll Finalize the SO at the end)";
}

/** Seal one box: records it in the shipment system and gives its label. */
export async function sealBox({ plan, palletId, boxId, boxLabel, ui, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return { done: false };
  const name = palletDisplayName(plan, palletId);
  const opensPdf = !!ui.openPdfTab;
  if (!(await ui.confirm(sealBoxQuestion(boxLabel, name, { opensPdf, printer: labelStation(ui.labelStation).printer }), { title: `Seal ${boxLabel}?`, confirmLabel: 'Seal box' }))) return { done: false };
  const sealReq = await sealBody(ui);
  if (!sealReq) {
    setMessage(SEAL_NEEDS_SIGN_IN);
    return { done: false, message: SEAL_NEEDS_SIGN_IN };
  }
  setBusy(true); setMessage('');
  try {
    const body = await postOrOverride(
      `/packaging/plans/${plan.plan_id}/pallets/${palletId}/boxes/${boxId}/seal`,
      sealReq, ui, 'seal',
    );
    const labels = { path: body.labels_url, filename: `${boxLabel}_${name}_${plan.order_number || 'Label'}.pdf`, what: `${boxLabel} label` };
    if (opensPdf) await ui.openPdfTab(`${API_BASE}${body.labels_url}`, labels.filename);
    const message = body.already_sealed
      ? `${boxLabel} was already sealed${opensPdf ? ' — opened the existing label' : ''}.`
      : `${boxLabel} sealed on Pallet ${name}.`;
    setMessage(message);
    await reloadPlan();
    return { done: true, message, labels };
  } catch (err) {
    const message = `Seal ${boxLabel} failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** Seal a pallet (the finisher): seals what is left, records loose stacks, marks it Ready. */
export async function sealPallet({ plan, palletId, ui, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return { done: false };
  const name = palletDisplayName(plan, palletId);
  const opensPdf = !!ui.openPdfTab;
  if (!(await ui.confirm(sealPalletQuestion(name, plan.order_number, { opensPdf, printer: labelStation(ui.labelStation).printer }), { title: `Seal ${name}?`, confirmLabel: 'Seal pallet' }))) return { done: false };
  const sealReq = await sealBody(ui);
  if (!sealReq) {
    setMessage(SEAL_NEEDS_SIGN_IN);
    return { done: false, message: SEAL_NEEDS_SIGN_IN };
  }
  setBusy(true); setMessage('');
  try {
    const body = await postOrOverride(`/packaging/plans/${plan.plan_id}/pallets/${palletId}/seal`, sealReq, ui, 'seal');
    const labels = { path: body.labels_url, filename: `Pallet_${name}_${plan.order_number || 'Labels'}.pdf`, what: `${name} pallet + stack labels` };
    if (opensPdf) await ui.openPdfTab(`${API_BASE}${body.labels_url}`, labels.filename);
    let message;
    if (body.already_sealed) {
      message = `Pallet ${name} was already sealed${opensPdf ? ' — opened the existing label PDF' : ''}.`;
    } else {
      const parts = [`Pallet ${name} sealed`];
      if (body.auto_sealed_box_count) parts.push(`${body.auto_sealed_box_count} box(es) auto-sealed`);
      if (body.pallet_direct_item_count) parts.push(`${body.pallet_direct_item_count} stack(s) added`);
      message = parts.join(' · ');
    }
    setMessage(message);
    await reloadPlan();
    return { done: true, message, labels };
  } catch (err) {
    const message = `Seal Pallet ${name} failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** "Ship this pallet as it is" (jobq-api holding pallets, 2026-10-27): a holding pallet (a laser task's WIP pallet)
 *  becomes an ordinary Building shipping pallet with its pieces and beacon; then it is sealed, staged and shipped as
 *  any other. POST .../promote {edited_by}. A 409 for an in-house step still owed is the seal path's own refusal
 *  (postOrOverride offers the manager override); any other 409 (still being cut, another order's pieces) is shown.
 *  The plan is re-read BEFORE the message is set (a reload clears the page's message). */
export async function promoteHoldingPallet({ plan, palletId, ui, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return { done: false };
  const name = palletDisplayName(plan, palletId);
  const ok = await ui.confirm(
    `Ship ${name} as it is?\n\nIt becomes a shipping pallet for ${plan.order_number || 'this order'}: its pieces and its `
    + 'beacon stay on it, nothing is re-handled. Then seal, stage and ship it as any other pallet.',
    { title: `Ship ${name} as it is?`, confirmLabel: 'Ship as it is' },
  );
  if (!ok) return { done: false };
  setBusy(true); setMessage('');
  try {
    const body = await postOrOverride(
      `/packaging/plans/${plan.plan_id}/pallets/${palletId}/promote`,
      withEditor({}), ui, 'ship as it is',
    );
    await reloadPlan();
    const message = promotedText(body, name);
    setMessage(message);
    return { done: true, message, result: body };
  } catch (err) {
    const message = `Couldn't ship ${name} as it is: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** "Pack into…" from a holding-pallet card (Prepare shipment): manual-assign with the card's source fields
 *  (from_wip_pallet_id + from_wip_assembly_id for an assembly row, from_wip_raw for a plain row; an older API ignores
 *  them). A 409 (more than this pallet holds, someone packed it meanwhile) shows the API's sentence AND re-reads the
 *  plan, so the card shows what is really there. The plan is re-read before the message is set (a reload clears it).
 *  Resolves to the manual-assign answer, or null when refused. */
export async function packFromHoldingPallet({ plan, body, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return null;
  setBusy(true); setMessage('');
  try {
    const res = await postJson(`/packaging/plans/${plan.plan_id}/manual-assign`, withEditor(body));
    if (res.status === 409) {
      const b = await res.json().catch(() => ({}));
      const message = detailToString(b.detail) || 'This pallet no longer holds that many: the plan was re-read.';
      await reloadPlan();
      setMessage(message);
      return null;
    }
    const answer = await readOk(res);
    await reloadPlan();
    const note = wipDrainNote(answer);
    if (note) setMessage(note);
    return answer;
  } catch (err) {
    setMessage(errText(err));
    return null;
  } finally {
    setBusy(false);
  }
}

// Ship and stage share their tail: the last pallet hands off to the finalize email (same PIN);
// otherwise the per-pallet manifest opens (desktop) or is offered (phone).
async function finishShipOrStage({ plan, palletId, pin, body, bits, lastKey, kind, manifestPrefix, ui, setMessage, reloadPlan, openFinalize }) {
  const name = palletDisplayName(plan, palletId);
  if (body[lastKey]) {
    const message = bits.join(' · ');
    setMessage(message);
    await reloadPlan();
    const finalize = { kind, pin, preActionBits: bits };
    openFinalize(finalize);
    return { done: true, message, finalize };
  }
  let document = null;
  if (body.manifest_url) {
    document = { url: `${API_BASE}${body.manifest_url}`, filename: `${manifestPrefix}${name}_${plan.order_number || 'Plan'}.pdf`, what: `${name} manifest` };
    if (ui.openPdfTab) {
      try { await ui.openPdfTab(document.url, document.filename); } catch (_) { /* non-fatal */ }
    }
  }
  const message = bits.join(' · ');
  setMessage(message);
  await reloadPlan();
  return { done: true, message, document };
}

/** jobq-api (2026-10-28): a plain ship of a delivery order with delivery signing on answers the pallet's signing
 *  code as `sign` (Load truck's shape: url, short_code, qr_svg...), or `sign_error` when none could be made (the
 *  pallet shipped anyway). The sentence for the ship's message, or null (any other answer: exactly as before). */
export function deliverySignBit(body) {
  if (!body) return null;
  if (body.sign && body.sign.short_code) return `Delivery signing code ${body.sign.short_code} (on the printed manifest)`;
  if (body.sign_error) return String(body.sign_error);
  return null;
}

/** Ship a sealed pallet: PIN, email the customer or not, finalize its Shipping tasks. */
export async function shipPallet({ plan, palletId, ui, setBusy = noop, setMessage = noop, reloadPlan = noop, openFinalize = noop }) {
  if (!plan) return { done: false };
  const name = palletDisplayName(plan, palletId);
  // the load-out checklist first: pull the beacon, check the pallet off as it goes on the truck
  setBusy(true);                       // no second click while the list is open
  const lo = await loadOut({ planId: plan.plan_id, step: 'ship', palletIds: [palletId], recordLabel: 'Loaded: ship it' });
  setBusy(false);
  if (lo.cancelled) return { done: false };
  const pin = await ui.pin(`Enter your 4-digit PIN to ship Pallet ${name} for ${plan.order_number}.`);
  if (!pin) return { done: false };
  const notify = await askNotify(ui, SHIP_NOTIFY_QUESTION, SHIP_NOTIFY_ASK);
  if (notify === null) return { done: false };
  setBusy(true); setMessage('');
  try {
    const body = await postOrOverride(`/packaging/plans/${plan.plan_id}/pallets/${palletId}/ship`, withLoad({
      pin,
      ship_date: shopToday(), // the shop's (Central) day, not UTC's (Mike, 2026-10-14)
      notify_customer: notify,
      // fulfillment_method omitted — the API resolves it from sales_orders.fulfillment_type, so a
      // pickup order's email says "Ready for Pickup".
    }, lo), ui, 'ship');
    const bits = [];
    if (body.already_shipped) {
      bits.push(`Pallet ${name} was already shipped`);
    } else {
      bits.push(`Pallet ${name} shipped`);
      if (loadedNote(body)) bits.push(loadedNote(body));
      if (body.processed_task_count) bits.push(`${body.processed_task_count} task(s) finalized`);
      if (body.auto_closed_count) bits.push(`${body.auto_closed_count} surplus task(s) auto-closed`);
    }
    const signBit = deliverySignBit(body);
    if (signBit) bits.push(signBit);
    const out = await finishShipOrStage({
      plan, palletId, pin, body, bits, lastKey: 'plan_completed', kind: 'shipping', manifestPrefix: 'Manifest_',
      ui, setMessage, reloadPlan, openFinalize,
    });
    return body.sign ? { ...out, sign: body.sign } : out;
  } catch (err) {
    const message = `Ship Pallet ${name} failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** Mark a sealed pallet ready for customer pickup: PIN, email the customer or not. */
export async function stagePallet({ plan, palletId, ui, setBusy = noop, setMessage = noop, reloadPlan = noop, openFinalize = noop }) {
  if (!plan) return { done: false };
  const name = palletDisplayName(plan, palletId);
  const pin = await ui.pin(`Enter your 4-digit PIN to mark pallet ${name} ready for pickup.`);
  if (!pin) return { done: false };
  const notify = await askNotify(ui, stageNotifyQuestion(name), stageNotifyAsk(name));
  if (notify === null) return { done: false };
  setBusy(true); setMessage('');
  try {
    const body = await postOrOverride(`/packaging/plans/${plan.plan_id}/pallets/${palletId}/stage`, { pin, notify_customer: notify }, ui, 'stage');
    const bits = [];
    if (body.already_staged) {
      bits.push(`Pallet ${name} was already staged`);
    } else {
      bits.push(`Pallet ${name} staged for pickup`);
      if (body.processed_task_count) bits.push(`${body.processed_task_count} task(s) finalized`);
    }
    return await finishShipOrStage({
      plan, palletId, pin, body, bits, lastKey: 'plan_ready_for_pickup', kind: 'pickup', manifestPrefix: 'Pickup_Manifest_',
      ui, setMessage, reloadPlan, openFinalize,
    });
  } catch (err) {
    const message = `Stage ${name} failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** The customer is here: a staged pallet becomes Shipped (no email, no quantity work). */
export async function confirmPickup({ plan, palletId, ui, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return { done: false };
  const name = palletDisplayName(plan, palletId);
  setBusy(true);
  const lo = await loadOut({ planId: plan.plan_id, step: 'pickup', palletIds: [palletId], recordLabel: 'Loaded: confirm pickup' });
  setBusy(false);
  if (lo.cancelled) return { done: false };
  const pin = await ui.pin(`Customer is here — enter your 4-digit PIN to confirm pickup of pallet ${name}.`);
  if (!pin) return { done: false };
  setBusy(true); setMessage('');
  try {
    const body = await readOk(await postJson(`/packaging/plans/${plan.plan_id}/pallets/${palletId}/confirm-pickup`,
      withLoad({ pin }, lo)));
    let message;
    if (body.already_picked_up) {
      message = `Pallet ${name} was already marked picked up.`;
    } else {
      const bits = [`Pallet ${name} picked up`];
      if (loadedNote(body)) bits.push(loadedNote(body));
      if (body.plan_completed) bits.push('plan → Complete');
      message = bits.join(' · ');
    }
    setMessage(message);
    await reloadPlan();
    return { done: true, message };
  } catch (err) {
    const message = `Confirm pickup ${name} failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** The customer is here for the whole order: every staged pallet becomes Shipped. */
export async function confirmPickupAll({ plan, stagedCount, ui, setBusy = noop, setMessage = noop, reloadPlan = noop }) {
  if (!plan) return { done: false };
  // the load-out checklist: the customer may take only some of the staged pallets (each one ticked is
  // confirmed on its own; all of them = the whole-order call)
  // the server's own list of staged pallets decides "some" or "all" (the page's copy can be stale)
  setBusy(true);
  const lo = await loadOut({ planId: plan.plan_id, step: 'pickup', requireAll: false, recordLabel: 'Loaded: confirm pickup' });
  setBusy(false);
  if (lo.cancelled) return { done: false };
  const partial = Array.isArray(lo.items) && lo.items.length < (lo.total || 0);
  const count = partial ? lo.items.length : stagedCount;
  const pin = await ui.pin(partial
    ? `Customer is here — enter your 4-digit PIN to confirm pickup of the ${count} pallet(s) checked off.`
    : `Customer here for the whole order — enter your 4-digit PIN to confirm pickup of all ${stagedCount} staged pallet(s).`);
  if (!pin) return { done: false };
  setBusy(true); setMessage('');
  try {
    if (partial) {
      // one confirm per loaded pallet, in order; one failure stops the rest (the message says which)
      let done = 0;
      let loaded = 0;
      let planCompleted = false;
      for (const it of lo.items) {
        const name = palletDisplayName(plan, it.pallet_id);
        let b;
        try {
          b = await readOk(await postJson(`/packaging/plans/${plan.plan_id}/pallets/${it.pallet_id}/confirm-pickup`,
            { pin, load_checklist: [it] }));
        } catch (err) {
          const message = `Confirmed ${done} of ${lo.items.length}; pallet ${name} failed: ${errText(err)}`;
          setMessage(message);
          await reloadPlan();
          return { done: false, message };
        }
        done += 1;
        loaded += (b.load_checklist && Array.isArray(b.load_checklist.recorded)) ? b.load_checklist.recorded.length : 0;
        if (b.plan_completed) planCompleted = true;
      }
      const bits = [`Confirmed pickup of ${done} pallet(s)`];
      if (loaded) bits.push(`${loaded} checked off as loaded`);
      if (planCompleted) bits.push('plan → Complete');
      const message = bits.join(' · ');
      setMessage(message);
      await reloadPlan();
      return { done: true, message };
    }
    const body = await readOk(await postJson(`/packaging/plans/${plan.plan_id}/confirm-pickup-all`, withLoad({ pin }, lo)));
    const bits = [`Confirmed pickup of ${body.confirmed_pallet_count} pallet(s)`];
    if (loadedNote(body)) bits.push(loadedNote(body));
    if (body.plan_completed) bits.push('plan → Complete');
    const message = bits.join(' · ');
    setMessage(message);
    await reloadPlan();
    return { done: true, message };
  } catch (err) {
    const message = `Confirm pickup all failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

// --- Reprints ---------------------------------------------------------------------------------
// The desktop opens the PDF (ui.openPdfTab); a phone sends it through the shop print queue to the station its ui
// names (`ui.labelStation`, the phone Shipping screen's), else the Laser Zebra (docs/mobile-pwa-plan.md "Print audit").

export const palletLabelsPath = (shipmentGroupId) => `/reports/pallet_labels/batch/${shipmentGroupId}`;
export const boxLabelPath = (shipmentGroupId, shipmentBoxId) => `/reports/pallet_labels/batch/${shipmentGroupId}?box_id=${shipmentBoxId}`;

async function labelOut(ui, { path, filename, kind, what }) {
  if (ui.openPdfTab) {
    await ui.openPdfTab(`${API_BASE}${path}`, filename);
    return null;
  }
  // a phone: the station its ui names (the phone Shipping screen's ../print/labelStation), else the laser
  return printLabelsAt({ station: ui.labelStation, path, kind, what, trigger: ui.printTrigger });
}

/** Reprint a sealed pallet's pallet + box + stack labels. */
export async function reprintPalletLabels({ shipmentGroupId, palletNum, palletName, ui, setBusy = noop, setMessage = noop }) {
  if (!shipmentGroupId) return { done: false };
  setBusy(true); setMessage('');
  try {
    const queued = await labelOut(ui, {
      path: palletLabelsPath(shipmentGroupId), filename: `Pallet_P${palletNum}_Labels.pdf`,
      kind: 'pallet_labels', what: `${palletName || `P${palletNum}`} labels`,
    });
    if (queued) setMessage(queued.message);
    return { done: true, message: queued?.message };
  } catch (err) {
    const message = `Reprint labels failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** Reprint one sealed box's label (the same PDF its seal printed). */
export async function reprintBoxLabel({ shipmentGroupId, shipmentBoxId, boxLabel, ui, setBusy = noop, setMessage = noop }) {
  if (!shipmentGroupId || !shipmentBoxId) {
    const message = `Can't reprint ${boxLabel} — no shipment record found. Seal the box first.`;
    setMessage(message);
    return { done: false, message };
  }
  setBusy(true); setMessage('');
  try {
    const queued = await labelOut(ui, {
      path: boxLabelPath(shipmentGroupId, shipmentBoxId), filename: `${boxLabel}_Label.pdf`,
      kind: 'pallet_labels', what: `${boxLabel} label`,
    });
    if (queued) setMessage(queued.message);
    return { done: true, message: queued?.message };
  } catch (err) {
    const message = `Reprint ${boxLabel} failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

/** The manifest for ONE pallet (its shipment group), or the legacy batch manifest without a plan. */
export function palletManifestDocument({ plan, shipmentGroupId, palletNum }) {
  if (plan && plan.plan_id) {
    const pal = (plan.pallets || []).find((p) => p.shipment_group_id === shipmentGroupId);
    const palName = pal?.short_id || `P${palletNum}`;
    return {
      url: `${API_BASE}/packaging/plans/${plan.plan_id}/manifest.pdf?shipment_group_id=${encodeURIComponent(shipmentGroupId)}`,
      filename: `Manifest_${palName}_${plan.order_number || plan.plan_id}.pdf`,
      what: `${palName} manifest`,
    };
  }
  return { url: `${API_BASE}/reports/manifest/batch/${shipmentGroupId}`, filename: `Manifest_P${palletNum}.pdf`, what: `P${palletNum} manifest` };
}

/** Reprint one pallet's manifest (desktop: opens it). */
export async function reprintManifest({ plan, shipmentGroupId, palletNum, ui, setBusy = noop, setMessage = noop }) {
  if (!shipmentGroupId) return { done: false };
  setBusy(true); setMessage('');
  try {
    const doc = palletManifestDocument({ plan, shipmentGroupId, palletNum });
    if (ui.openPdfTab) await ui.openPdfTab(doc.url, doc.filename);
    return { done: true, document: doc };
  } catch (err) {
    const message = `Reprint manifest failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

// --- The shop print queue ----------------------------------------------------------------------

/** Queue a label PDF at a print station's ZP 505 (POST /print/queue; `station` = an id from ../print/labelStation,
 *  the laser when missing or unknown). Throws on a refusal. */
export async function queueAtStation({ station, kind, pdfPath, trigger }) {
  const res = await fetch(`${API_BASE}/print/queue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ station: labelStation(station).id, kind, pdf_url: pdfPath, params: { trigger } }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}${txt ? ': ' + txt.slice(0, 200) : ''}`);
  }
  return res.json().catch(() => ({}));
}

/** Queue a label PDF at the laser's ZP 505 (POST /print/queue). Throws on a refusal. */
export function queueAtLaser({ kind, pdfPath, trigger }) {
  return queueAtStation({ station: 'laser', kind, pdfPath, trigger });
}

/** A phone's label print, queued at `station` (the laser when missing). Resolves to {printJobId, message}; throws. */
export async function printLabelsAt({ station, path, kind = 'pallet_labels', what = 'Labels', trigger = 'phone_sales_order' }) {
  const st = labelStation(station);
  const data = await queueAtStation({ station: st.id, kind, pdfPath: path, trigger });
  const jobId = data.print_job_id ? ` #${data.print_job_id}` : '';
  return { printJobId: data.print_job_id || null, message: `${what} sent to the ${st.printer}${jobId}. If nothing prints in ~30s, check the ${st.pi}.` };
}

/** A phone's label print: queued at the Laser Zebra. Resolves to {printJobId, message}; throws. */
export function printLabelsAtLaser({ path, kind = 'pallet_labels', what = 'Labels' }) {
  return printLabelsAt({ station: 'laser', path, kind, what });
}

/** A current-state status label for a pallet, box or stack at a ZP 505 (`station`: the laser when missing). */
export async function queueStatusLabel({ kind, idLabel, pdfPath, trigger = 'so_packaging_card_button', station = 'laser', setBusy = noop, setMessage = noop }) {
  setBusy(true); setMessage('');
  try {
    const st = labelStation(station);
    const data = await queueAtStation({ station: st.id, kind, pdfPath, trigger });
    const jobId = data.print_job_id ? `#${data.print_job_id}` : '';
    const message = st.id === 'laser'
      ? `✓ Status label queued ${jobId} → ZP 505. If nothing prints in ~30s, check the laser-pi.`
      : `✓ Status label queued ${jobId} → the ${st.printer}. If nothing prints in ~30s, check the ${st.pi}.`;
    setMessage(message);
    return { done: true, message };
  } catch (err) {
    const message = `Print ${idLabel} status label failed: ${errText(err)}`;
    setMessage(message);
    return { done: false, message };
  } finally {
    setBusy(false);
  }
}

export const STATUS_LABELS = Object.freeze({
  pallet: (palletId) => ({ kind: 'pallet_status_label', pdfPath: `/reports/pallet_status_label/${palletId}.pdf` }),
  box: (boxId) => ({ kind: 'box_status_label', pdfPath: `/reports/box_status_label/${boxId}.pdf` }),
  stack: (boxId) => ({ kind: 'stack_status_label', pdfPath: `/reports/stack_status_label/${boxId}.pdf` }),
});
